import React, { useCallback, useLayoutEffect } from "react";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

import { FlashList } from "@shopify/flash-list";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { WANoteExistingQuery } from "./__generated__/WANoteExistingQuery.graphql";
import {
  AHButton,
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Circle,
  ListItem,
  ScaleFactorProvider,
  TouchableBox,
} from "arkh";

import { WANoteExistingSelectNoteQuery } from "./__generated__/WANoteExistingSelectNoteQuery.graphql";
import { RouteProp } from "@react-navigation/native";
import { WidgetNote } from "widgets";
import { WAPreviewContainer } from "../../WidgetAdd/WAPreviewContainer";
import { WACommon } from "../NavigationParamsWidgets";

import { WANoteExistingMutation } from "./__generated__/WANoteExistingMutation.graphql";

import { WANoteExistingSelectNote_widget$key } from "./__generated__/WANoteExistingSelectNote_widget.graphql";
import { ActivityIndicator, View } from "react-native";
import { HeaderButton } from "../../../../../components";

import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../../../../components/atoms/ListSectionHeader";
import { useListContentStyle } from "../../../../../components/mobile/useListContentStyle";
import { FormattedMessage, useIntl } from "react-intl";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { Lottie } from "../../../../../components/Lottie";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddNoteExisting">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddNoteExisting">;
};

type ParamList = {
  WANoteExistingSelectDashboard: WACommon;
  WANoteExistingSelectNote: {
    dashboardId: string;
  } & WACommon;
};

const Stack = createNativeStackNavigator<ParamList>();

export const WANoteExisting = withWebModal(WANoteExistingC, true);
function WANoteExistingC(props: Props) {
  const intl = useIntl();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="WANoteExistingSelectDashboard"
        component={WANoteExistingSelectDashboardScreen}
        initialParams={props.route.params}
        options={{
          headerLeft: (p) => (
            <HeaderButton
              type="BACK"
              onPress={() => {
                props.navigation.goBack();
              }}
            />
          ),
          headerTitle: intl.formatMessage({
            defaultMessage: "Select note to link",
            description: "WANoteExistingSelectDashboard header title",
          }),
        }}
      />
      <Stack.Screen
        name="WANoteExistingSelectNote"
        component={WANoteExistingSelectNote}
        options={{
          headerTitle: "",
        }}
      />
    </Stack.Navigator>
  );
}

function WANoteExistingSelectDashboardScreen(props: {
  navigation: NativeStackNavigationProp<
    ParamList,
    "WANoteExistingSelectDashboard"
  >;
  route: RouteProp<ParamList, "WANoteExistingSelectDashboard">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <WANoteExistingSelectDashboard {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}
function WANoteExistingSelectDashboard(props: {
  navigation: NativeStackNavigationProp<
    ParamList,
    "WANoteExistingSelectDashboard"
  >;
  route: RouteProp<ParamList, "WANoteExistingSelectDashboard">;
  fetchKey: string;
}) {
  const dashboards = useLazyLoadQuery<WANoteExistingQuery>(
    graphql`
      query WANoteExistingQuery {
        store {
          dashboards {
            id
            name
            widgets {
              ... on WidgetNote {
                id
                note {
                  id
                }
                integrationNoteEmaginFlyer {
                  id
                }
              }
            }
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const contentContainerStyle = useListContentStyle();

  const dashboardsWithNotes = dashboards.store.dashboards.filter(
    (d) =>
      d.id !== props.route.params.storecastId &&
      d.widgets.filter((w) => w?.id && !w.integrationNoteEmaginFlyer).length > 0
  );

  if (dashboardsWithNotes.length === 0) {
    return (
      <AHEmptyScreen
        title={
          <FormattedMessage
            description="WA Note Existing Empty Screen Title "
            defaultMessage="There's nothing in here"
          />
        }
        subtitle={
          <FormattedMessage
            description="WA Note Existing Empty Screen Subtitle "
            defaultMessage="Already existing Notes can be used on multiple boards. When you create 'Text and Image' widgets, you can add them to your existing boards, making it easy to share and reuse your notes."
          />
        }
        illustration={
          <Lottie
            autoPlay
            loop
            source={require("../../../../../assets/shake-a-empty-box.json")}
          />
        }
      />
    );
  }

  return (
    <FlashList
      contentContainerStyle={contentContainerStyle}
      data={dashboardsWithNotes}
      ItemSeparatorComponent={AHSeparator}
      ListHeaderComponent={
        <ListSectionHeader>
          <ListSectionHeaderTitle>
            <FormattedMessage
              description="WANoteExisting storecast list header"
              defaultMessage="StoreCasts with Note widgets"
            />
          </ListSectionHeaderTitle>
        </ListSectionHeader>
      }
      renderItem={({ item, index }) => {
        return (
          <ListItem
            isFirst={index === 0}
            isLast={dashboardsWithNotes.length === index + 1}
            onPress={() => {
              props.navigation.navigateDeprecated("WANoteExistingSelectNote", {
                ...props.route.params,
                dashboardId: item.id,
              });
            }}
            left={
              <Circle size="smallMedium" color="primary">
                <AHIcon type="dashboard" color="surface" size="small" />
              </Circle>
            }
            right={
              <>
                <AHText spaceRight="small" type="subtitle">
                  {
                    item.widgets
                      .filter(
                        (w) =>
                          w?.id && !w.integrationNoteEmaginFlyer && w.note?.id
                      )
                      .map((w) => w?.note?.id)
                      .filter(function onlyUnique(value, index, array) {
                        return array.indexOf(value) === index;
                      }).length
                  }
                </AHText>
                <AHIcon color="darkText" type="chevron-right" />
              </>
            }
          >
            <ListItem.Title>{item.name}</ListItem.Title>
          </ListItem>
        );
      }}
    />
  );
}

function WANoteExistingSelectNote(props: {
  route: RouteProp<ParamList, "WANoteExistingSelectNote">;
  navigation: NativeStackNavigationProp<ParamList, "WANoteExistingSelectNote">;
}) {
  const { dashboard } = useLazyLoadQuery<WANoteExistingSelectNoteQuery>(
    graphql`
      query WANoteExistingSelectNoteQuery($dashboardId: ID!) {
        dashboard(id: $dashboardId) {
          id
          name
          widgets {
            ... on WidgetNote {
              id

              note {
                id
              }
              integrationNoteEmaginFlyer {
                id
              }
              ...WANoteExistingSelectNote_widget

              __typename
            }
          }
        }
      }
    `,
    {
      dashboardId: props.route.params.dashboardId,
    }
  );

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerTitle: dashboard?.name ?? "",
    });
  }, [dashboard?.name, props.navigation]);

  const contentContainerStyle = useListContentStyle();

  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  return (
    <FlashList
      masonry
      contentContainerStyle={contentContainerStyle}
      numColumns={isCompact ? 1 : 2}
      data={(dashboard?.widgets ?? []).filter(
        (w, i, a) =>
          w &&
          w.__typename === "WidgetNote" &&
          !w.integrationNoteEmaginFlyer &&
          w.note &&
          a.findIndex(
            (w2) =>
              w2?.__typename === "WidgetNote" && w2.note?.id === w.note?.id
          ) === i
      )}
      renderItem={({ item, index }) => {
        if (item?.__typename !== "WidgetNote") {
          return null;
        }
        return (
          <Item item={item} navigation={props.navigation} route={props.route} />
        );
      }}
    />
  );
}

function Item(props: {
  item: WANoteExistingSelectNote_widget$key;
  route: RouteProp<ParamList, "WANoteExistingSelectNote">;
  navigation: NativeStackNavigationProp<ParamList, "WANoteExistingSelectNote">;
}) {
  const widget = useFragment(
    graphql`
      fragment WANoteExistingSelectNote_widget on WidgetNote {
        height
        note {
          id
        }
        ...WidgetNote_widgetNote
      }
    `,
    props.item
  );
  const doesFit = props.route.params.maxSize.h >= widget.height;
  const [createWidgetNote, creating] = useMutation<WANoteExistingMutation>(
    graphql`
      mutation WANoteExistingMutation(
        $storecastId: ID!
        $widget: CreateWidgetNoteFromExistingNoteInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetNoteFromExistingNote(widget: $widget) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (noteId: string) => {
      createWidgetNote({
        variables: {
          storecastId: props.route.params.storecastId,
          widget: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            noteId,
          },
        },
        onCompleted: () => {
          props.navigation.goBack();
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetNote,
      props.route.params.storecastId,
      props.route.params.position.x,
      props.route.params.position.c,
      props.route.params.position.y,
      props.navigation,
    ]
  );

  const intl_tokens = useIntlTokens();

  return (
    <TouchableBox
      style={{
        alignItems: "center",
        flex: 1,
      }}
      spaceVertical="medium"
      spaceBottom="xlarge"
      disabled={!doesFit || creating}
      onPress={() => {
        onCreate(widget.note.id);
      }}
    >
      <WAPreviewContainer
        h={widget.height}
        w={2}
        unitSize={props.route.params.unitSize}
        scale={0.8}
      >
        <WidgetNote
          mode="IDLE"
          widget={widget}
          storecastId={props.route.params.storecastId}
          interactive={false}
        />
        <Box
          space="large"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            flexDirection: "row-reverse",
          }}
        >
          <ScaleFactorProvider value={1}>
            {doesFit ? (
              <AHButton
                themeType="primary"
                labelStyle={{
                  textAlign: "center",
                }}
                onPress={() => {}}
                title={intl_tokens.Add}
              />
            ) : (
              <AHButton
                themeType="primary"
                disabled
                labelStyle={{
                  textAlign: "center",
                }}
                onPress={() => {}}
                title={
                  <FormattedMessage
                    defaultMessage="Does not fit"
                    description="WANoteExisting add existing disabled label"
                  />
                }
              />
            )}
          </ScaleFactorProvider>
        </Box>
        {!doesFit ? (
          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              top: 0,
              flexDirection: "row-reverse",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              backgroundColor: "rgba(255,255,255,0.4)",
            }}
          >
            <ScaleFactorProvider value={1} />
          </View>
        ) : null}
        {creating ? (
          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              top: 0,
              flexDirection: "row-reverse",
              justifyContent: "center",
              alignContent: "center",
              backgroundColor: "rgba(255,255,255,0.4)",
            }}
          >
            <ActivityIndicator color="#000" />
          </View>
        ) : null}
      </WAPreviewContainer>
    </TouchableBox>
  );
}
